import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationsPanel } from "./NotificationsPanel";
import { SubscriptionStatusBadge } from "@/components/sidebar/SubscriptionStatusBadge";
interface MobileNavBarProps {
  onMenuClick: () => void;
}

export function MobileNavBar({ onMenuClick }: MobileNavBarProps) {
  const { user } = useAuth();
  const { 
    notifications,
    unreadCount,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDismissNotification,
    handleClearAll,
    soundEnabled,
    handleToggleSound
  } = useNotifications();

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("company_name, logo_url, avatar_url")
        .eq("id", user?.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: keepPreviousData,
  });

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "S";
    return name.charAt(0).toUpperCase();
  };

  const companyName = profile?.company_name || "sendfeedback";

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/40 bg-sidebar">
      <div className="flex h-14 items-center justify-between px-4">
        {/* Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="min-h-[44px] min-w-[44px] text-foreground hover:bg-accent"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </Button>

        {/* Company Avatar + Name + Status */}
        <div className="flex items-center gap-2">
          <Avatar className="h-7 w-7 rounded-lg bg-primary">
            <AvatarImage src={profile?.logo_url || undefined} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium rounded-lg">
              {getInitials(companyName)}
            </AvatarFallback>
          </Avatar>
          <span className="text-[13px] font-medium text-foreground">
            {companyName.toLowerCase()}
          </span>
          <SubscriptionStatusBadge compact />
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center">
          <NotificationsPanel
            notifications={notifications}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onDismiss={handleDismissNotification}
            onClearAll={handleClearAll}
            soundEnabled={soundEnabled}
            onToggleSound={handleToggleSound}
          />
        </div>
      </div>
    </header>
  );
}
